<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import wootConstants from 'dashboard/constants/globals';
import { frontendURL } from 'dashboard/helper/URLHelper';
import {
  buildAvailableChannelFilters,
  buildInboxChannelFilters,
  channelIconForInbox,
  channelLabelKeyForInbox,
  channelPresentationForInbox,
  inboxFilterId,
} from './helpers/channel';
import {
  buildChannelCountMap,
  buildEssentialConversationFilters,
  filterConversationsByChannel,
  filterConversationsByTeamFocus,
} from './helpers/conversationFilters';
import EssentialActionPanel from './EssentialActionPanel.vue';
import EssentialHeaderMenu from './EssentialHeaderMenu.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const props = defineProps({
  conversationId: {
    type: [String, Number],
    default: 0,
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const selectedTeamFilter = ref('all');
const selectedChannelFilter = ref('all');
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const page = ref(1);
const essentialInboxes = ref([]);
const channelCounts = ref({});

const allConversations = computed(() => store.getters.getAllConversations);
const currentChat = computed(() => store.getters.getSelectedChat);
const isLoading = computed(() => store.getters.getChatListLoadingStatus);
const currentUser = computed(() => store.getters.getCurrentUser || {});
const currentAccount = computed(() => store.getters.getCurrentAccount || {});
const internalCommunicationEnabled = computed(() =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    route.params.accountId,
    FEATURE_FLAGS.INTERNAL_COMMUNICATION
  )
);
const internalUnreadCount = computed(
  () => store.getters['internalCommunication/getUnreadCount'] || 0
);
const agents = computed(() => store.getters['agents/getAgents'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);
const inboxes = computed(() => essentialInboxes.value || []);

const availableTeams = computed(() => {
  const currentAgent = agents.value.find(
    agent => Number(agent.id) === Number(currentUser.value.id)
  );
  const visibleTeamIds = currentAgent?.visible_team_ids || [];
  const baseTeams =
    currentAccount.value.role === 'administrator'
      ? teams.value
      : teams.value.filter(team => team.is_member);

  if (!visibleTeamIds.length) return baseTeams;

  return baseTeams.filter(team =>
    visibleTeamIds.map(String).includes(String(team.id))
  );
});

const teamFilterOptions = computed(() => [
  {
    id: 'all',
    icon: 'i-lucide-users-round',
    label: t('ESSENTIAL.FILTERS.ALL_MY_TEAMS'),
  },
  {
    id: 'mine',
    icon: 'i-lucide-user-round',
    label: t('ESSENTIAL.FILTERS.MY_CONVERSATIONS'),
  },
  {
    id: 'none',
    icon: 'i-lucide-circle-dashed',
    label: t('ESSENTIAL.FILTERS.NO_TEAM'),
  },
  ...availableTeams.value.map(team => ({
    id: String(team.id),
    icon: 'i-lucide-users',
    label: team.name,
  })),
]);

const statusFilters = computed(() => [
  {
    id: wootConstants.STATUS_TYPE.OPEN,
    icon: 'i-lucide-clock-3',
    label: t('ESSENTIAL.STATUS.OPEN'),
  },
  {
    id: wootConstants.STATUS_TYPE.PENDING,
    icon: 'i-lucide-pause-circle',
    label: t('ESSENTIAL.STATUS.PENDING'),
  },
  {
    id: wootConstants.STATUS_TYPE.ALL,
    icon: 'i-lucide-list-filter',
    label: t('ESSENTIAL.STATUS.ALL'),
  },
]);

const channelLabels = computed(() => ({
  'ESSENTIAL.CHANNELS.ALL': t('ESSENTIAL.CHANNELS.ALL'),
  'ESSENTIAL.CHANNELS.WHATSAPP': t('ESSENTIAL.CHANNELS.WHATSAPP'),
  'ESSENTIAL.CHANNELS.WHATSAPP_EVOLUTION': t(
    'ESSENTIAL.CHANNELS.WHATSAPP_EVOLUTION'
  ),
  'ESSENTIAL.CHANNELS.WHATSAPP_API': t('ESSENTIAL.CHANNELS.WHATSAPP_API'),
  'ESSENTIAL.CHANNELS.TELEGRAM': t('ESSENTIAL.CHANNELS.TELEGRAM'),
  'ESSENTIAL.CHANNELS.EMAIL': t('ESSENTIAL.CHANNELS.EMAIL'),
  'ESSENTIAL.CHANNELS.SITE': t('ESSENTIAL.CHANNELS.SITE'),
  'ESSENTIAL.CHANNELS.OTHER': t('ESSENTIAL.CHANNELS.OTHER'),
}));

const channelText = labelKey =>
  channelLabels.value[labelKey] ||
  channelLabels.value['ESSENTIAL.CHANNELS.OTHER'];

const channelFilters = computed(() =>
  [
    ...buildAvailableChannelFilters(inboxes.value),
    ...buildInboxChannelFilters(inboxes.value),
  ].map(channel => ({
    ...channel,
    label: channel.label || channelText(channel.labelKey),
  }))
);

const currentAccountRole = computed(() =>
  currentAccount.value.role === 'administrator'
    ? t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR')
    : t('AGENT_MGMT.AGENT_TYPES.AGENT')
);

const currentInterfaceMode = computed(() =>
  currentAccount.value.interface_mode === 'essential'
    ? t('AGENT_MGMT.INTERFACE_MODE.ESSENTIAL')
    : t('AGENT_MGMT.INTERFACE_MODE.PRO')
);

const currentUserContext = computed(() =>
  [
    currentUser.value.name || currentUser.value.email,
    currentAccountRole.value,
    currentInterfaceMode.value,
  ]
    .filter(Boolean)
    .join(' - ')
);

const inboxById = inboxId => {
  return inboxes.value.find(inbox => inbox.id === Number(inboxId)) || {};
};

const localTeamConversations = computed(() =>
  filterConversationsByTeamFocus(
    allConversations.value,
    selectedTeamFilter.value
  )
);

const visibleConversations = computed(() =>
  filterConversationsByChannel(
    localTeamConversations.value,
    inboxes.value,
    selectedChannelFilter.value
  )
);

const currentFilters = computed(() =>
  buildEssentialConversationFilters({
    teamFilter: selectedTeamFilter.value,
    status: activeStatus.value,
    page: page.value,
    channelFilter: selectedChannelFilter.value,
  })
);

const fetchEssentialInboxes = async () => {
  essentialInboxes.value = await store.dispatch('inboxes/getEssential');
};

const channelCount = channel => {
  const count = Number(channelCounts.value[channel.id]);
  return Number.isFinite(count) ? count : null;
};

const countFromMeta = meta => {
  const count = Number(meta?.all_count);
  return Number.isFinite(count) ? count : null;
};

const fetchMetaCount = async channelFilter => {
  const filters = buildEssentialConversationFilters({
    teamFilter: selectedTeamFilter.value,
    status: activeStatus.value,
    page: 1,
    channelFilter,
  });
  const { data } = await ConversationAPI.meta(filters);
  return countFromMeta(data?.meta);
};

const fetchChannelCounts = async () => {
  if (!inboxes.value.length) {
    channelCounts.value = {};
    return;
  }

  const requests = [
    ['all', 'all'],
    ...inboxes.value.map(inbox => [inbox.id, inboxFilterId(inbox.id)]),
  ];

  const results = await Promise.allSettled(
    requests.map(async ([key, filter]) => [key, await fetchMetaCount(filter)])
  );

  const inboxCounts = {};
  let allCount = null;

  results.forEach(result => {
    if (result.status !== 'fulfilled') return;

    const [key, count] = result.value;
    if (count === null) return;

    if (key === 'all') {
      allCount = count;
    } else {
      inboxCounts[key] = count;
    }
  });

  channelCounts.value = buildChannelCountMap({
    inboxes: inboxes.value,
    inboxCounts,
    allCount,
    channelFilters: channelFilters.value,
  });
};

const activeConversationCount = computed(() => {
  const count = channelCount({ id: selectedChannelFilter.value });
  if (count !== null) return count;

  return visibleConversations.value.length;
});

const selectedConversation = computed(() => {
  return allConversations.value.find(
    conversation => conversation.id === Number(props.conversationId)
  );
});

const senderName = conversation => {
  return (
    conversation?.meta?.sender?.name ||
    conversation?.meta?.sender?.email ||
    t('ESSENTIAL.CONVERSATION.UNKNOWN_CONTACT')
  );
};

const lastMessage = conversation => {
  const messages = conversation?.messages || [];
  return (
    conversation?.last_non_activity_message ||
    messages[messages.length - 1] ||
    {}
  );
};

const lastMessageText = conversation => {
  return (
    lastMessage(conversation).content || t('ESSENTIAL.CONVERSATION.NO_MESSAGE')
  );
};

const formatTime = timestamp => {
  if (!timestamp) return '';
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp * 1000));
};

const teamLabel = conversation => {
  return conversation?.meta?.team?.name || t('ESSENTIAL.FILTERS.NO_TEAM');
};

const channelLabel = conversation => {
  return channelText(channelLabelKeyForInbox(inboxById(conversation.inbox_id)));
};

const channelIcon = conversation => {
  return channelIconForInbox(inboxById(conversation.inbox_id));
};

const channelStyle = conversation =>
  channelPresentationForInbox(inboxById(conversation.inbox_id));

const fetchConversations = async () => {
  store.dispatch('setChatListFilters', currentFilters.value);
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch(
    'setChatSortFilter',
    wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC
  );
  await store.dispatch('fetchAllConversations');
};

const resetAndFetch = async () => {
  page.value = 1;
  await store.dispatch('emptyAllConversations');
  await fetchConversations();
  await fetchChannelCounts();
};

const loadMore = async () => {
  page.value += 1;
  await fetchConversations();
};

const setTeamFilter = async filterId => {
  selectedTeamFilter.value = String(filterId);
  await resetAndFetch();
};

const setStatusFilter = async status => {
  activeStatus.value = status;
  await resetAndFetch();
};

const setChannelFilter = async filterId => {
  selectedChannelFilter.value = String(filterId);
  await resetAndFetch();
};

const openConversation = conversation => {
  router.push(
    frontendURL(
      `accounts/${route.params.accountId}/essential/conversations/${conversation.id}`
    )
  );
};

const setActiveConversation = async () => {
  if (!props.conversationId) {
    store.dispatch('clearSelectedState');
    return;
  }

  const conversation = selectedConversation.value;

  if (!conversation) {
    store.dispatch('clearSelectedState');
    return;
  }

  if (!conversation || conversation.id === currentChat.value?.id) return;

  await store.dispatch('setActiveChat', {
    data: conversation,
    after: route.query.messageId,
  });
};

watch(
  () => props.conversationId,
  () => setActiveConversation()
);

watch(allConversations, () => setActiveConversation());

onBeforeRouteLeave(() => {
  if (props.conversationId) {
    store.dispatch('clearSelectedState');
  }
});

onMounted(async () => {
  await store.dispatch('internalCommunication/reset');
  await Promise.allSettled([
    store.dispatch('agents/get'),
    store.dispatch('teams/get'),
    fetchEssentialInboxes(),
    store.dispatch('portals/index'),
  ]);
  await resetAndFetch();
  if (internalCommunicationEnabled.value) {
    await store.dispatch('internalCommunication/fetchRooms');
  }
  await setActiveConversation();
});
</script>

<template>
  <section class="flex w-full h-full min-w-0 bg-n-surface-1 text-n-slate-12">
    <aside
      class="flex flex-col flex-shrink-0 w-full border-r border-n-weak bg-n-surface-1 sm:w-[360px] 2xl:w-[420px]"
      :class="{ 'hidden lg:flex': conversationId }"
    >
      <header class="flex flex-col gap-4 p-5 border-b border-n-weak">
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center min-w-0 gap-3">
            <span
              class="flex items-center justify-center flex-shrink-0 rounded-xl size-10 bg-n-teal-9 text-white"
            >
              <i class="i-ri-chat-1-fill size-5" />
            </span>
            <div class="min-w-0">
              <h1 class="m-0 text-lg font-semibold truncate text-n-slate-12">
                {{ $t('ESSENTIAL.HEADER.TITLE') }}
              </h1>
              <p class="m-0 text-xs font-medium text-n-teal-10">
                {{ $t('ESSENTIAL.HEADER.SUBTITLE') }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <router-link
              v-if="internalCommunicationEnabled"
              class="relative flex h-8 items-center gap-1.5 rounded-lg border border-n-weak px-2 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2"
              :to="
                frontendURL(`accounts/${route.params.accountId}/essential/team`)
              "
              :title="$t('INTERNAL_COMMUNICATION.NAVIGATION.LABEL')"
            >
              <i class="i-lucide-users-round size-4" />
              <span class="hidden sm:inline">
                {{ $t('INTERNAL_COMMUNICATION.NAVIGATION.LABEL') }}
              </span>
              <span
                v-if="internalUnreadCount"
                class="flex min-w-4 items-center justify-center rounded-full bg-n-brand px-1 text-[10px] font-semibold text-white"
              >
                {{
                  internalUnreadCount > 99
                    ? $t('INTERNAL_COMMUNICATION.NAVIGATION.BADGE_OVERFLOW')
                    : internalUnreadCount
                }}
              </span>
            </router-link>
            <EssentialHeaderMenu
              :account-id="route.params.accountId"
              :is-administrator="currentAccount.role === 'administrator'"
            />
          </div>
        </div>

        <div
          class="inline-flex max-w-full items-center gap-1.5 self-start rounded-md bg-n-slate-3 px-2 py-1 text-xs font-medium text-n-slate-11"
        >
          <i class="i-lucide-user-round size-3.5 flex-shrink-0" />
          <span class="truncate">{{ currentUserContext }}</span>
        </div>

        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="status in statusFilters"
            :key="status.id"
            type="button"
            class="inline-flex items-center justify-center min-w-0 gap-1.5 rounded-lg border border-solid px-2 py-2 text-xs font-medium"
            :class="
              activeStatus === status.id
                ? 'border-n-brand bg-n-brand/10 text-n-brand'
                : 'border-n-weak text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2'
            "
            @click="setStatusFilter(status.id)"
          >
            <i class="size-3.5 flex-shrink-0" :class="[status.icon]" />
            <span class="truncate">{{ status.label }}</span>
          </button>
        </div>

        <label class="flex flex-col gap-1 mb-0">
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('ESSENTIAL.FILTERS.TEAM_LABEL') }}
          </span>
          <select
            v-model="selectedTeamFilter"
            class="w-full !mb-0 text-sm"
            @change="setTeamFilter(selectedTeamFilter)"
          >
            <option
              v-for="option in teamFilterOptions"
              :key="option.id"
              :value="option.id"
            >
              {{ option.label }}
            </option>
          </select>
        </label>

        <div class="flex flex-wrap gap-2">
          <button
            v-for="channel in channelFilters"
            :key="channel.id"
            type="button"
            class="inline-flex items-center min-w-0 gap-1.5 rounded-lg border border-solid px-2 py-1.5 text-xs font-medium"
            :class="
              selectedChannelFilter === channel.id
                ? channel.activeClass
                : channel.inactiveClass
            "
            @click="setChannelFilter(channel.id)"
          >
            <span
              class="flex items-center justify-center flex-shrink-0 rounded-md size-6"
              :class="[channel.iconContainerClass]"
            >
              <i
                class="size-4 flex-shrink-0"
                :class="[channel.icon, channel.iconClass]"
              />
            </span>
            <span class="truncate">{{ channel.label }}</span>
            <span
              v-if="channel.badge"
              class="rounded bg-n-alpha-2 px-1 text-[10px] font-semibold"
              :class="[channel.iconClass]"
            >
              {{ channel.badge }}
            </span>
            <span
              v-if="channelCount(channel) !== null"
              class="flex items-center justify-center h-5 min-w-5 rounded-full bg-n-alpha-2 px-1 text-[11px]"
              :class="[channel.countClass]"
            >
              {{ channelCount(channel) }}
            </span>
          </button>
        </div>
      </header>

      <div class="flex-1 min-h-0 overflow-y-auto">
        <button
          v-for="conversation in visibleConversations"
          :key="conversation.id"
          type="button"
          class="flex w-full gap-3 px-5 py-4 text-left border-b border-solid border-n-weak hover:bg-n-alpha-2"
          :class="
            Number(conversationId) === conversation.id
              ? 'bg-n-amber-3/60 border-l-2 border-l-n-amber-9'
              : 'border-l-2 border-l-transparent'
          "
          @click="openConversation(conversation)"
        >
          <span
            class="flex items-center justify-center flex-shrink-0 mt-1 rounded-lg size-9 bg-n-slate-3 text-n-slate-11"
            :class="[channelStyle(conversation).iconContainerClass]"
          >
            <i
              class="size-5"
              :class="[
                channelIcon(conversation),
                channelStyle(conversation).iconClass,
              ]"
            />
          </span>
          <span class="flex flex-col min-w-0 flex-1 gap-1">
            <span class="flex items-start justify-between gap-3">
              <span class="font-semibold truncate text-sm text-n-slate-12">
                {{ senderName(conversation) }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ formatTime(conversation.last_activity_at) }}
              </span>
            </span>
            <span class="text-sm truncate text-n-slate-11">
              {{ lastMessageText(conversation) }}
            </span>
            <span class="flex flex-wrap gap-1.5 pt-1">
              <span
                class="inline-flex max-w-[140px] items-center gap-1 rounded-md bg-n-slate-3 px-2 py-0.5 text-xs font-medium text-n-slate-11"
              >
                <i class="i-lucide-users size-3" />
                <span class="truncate">{{ teamLabel(conversation) }}</span>
              </span>
              <span
                class="inline-flex max-w-[150px] items-center gap-1 rounded-md bg-n-slate-3 px-2 py-0.5 text-xs font-medium text-n-slate-11"
                :class="[channelStyle(conversation).tagClass]"
              >
                <i
                  class="size-3"
                  :class="[
                    channelIcon(conversation),
                    channelStyle(conversation).iconClass,
                  ]"
                />
                <span class="truncate">{{ channelLabel(conversation) }}</span>
              </span>
              <span
                v-if="conversation.unread_count"
                class="inline-flex items-center justify-center h-5 min-w-5 rounded-full bg-n-teal-9 px-1.5 text-xs font-semibold text-white"
              >
                {{ conversation.unread_count }}
              </span>
            </span>
          </span>
        </button>

        <div
          v-if="!isLoading && !visibleConversations.length"
          class="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center"
        >
          <i class="i-lucide-inbox size-8 text-n-slate-10" />
          <p class="m-0 text-sm font-medium text-n-slate-12">
            {{ $t('ESSENTIAL.CONVERSATION.EMPTY_TITLE') }}
          </p>
          <p class="m-0 text-sm text-n-slate-11">
            {{ $t('ESSENTIAL.CONVERSATION.EMPTY_DESC') }}
          </p>
        </div>

        <div v-if="isLoading" class="flex justify-center py-5">
          <Spinner class="text-n-brand" />
        </div>
      </div>

      <footer
        class="flex items-center justify-between gap-3 p-4 border-t border-n-weak"
      >
        <span class="text-sm text-n-slate-11">
          {{
            $t('ESSENTIAL.CONVERSATION.COUNT', {
              count: activeConversationCount,
            })
          }}
        </span>
        <Button
          slate
          faded
          sm
          icon="i-lucide-chevron-down"
          :label="$t('ESSENTIAL.CONVERSATION.LOAD_MORE')"
          :is-loading="isLoading"
          @click="loadMore"
        />
      </footer>
    </aside>

    <div
      class="hidden min-w-0 flex-1 flex-col lg:flex"
      :class="{ '!flex': conversationId }"
    >
      <div
        v-if="conversationId"
        class="flex items-center gap-2 border-b border-n-weak bg-n-surface-1 px-3 py-2 lg:hidden"
      >
        <router-link
          class="inline-flex items-center justify-center flex-shrink-0 border border-solid rounded-lg size-9 border-n-weak text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2"
          :to="frontendURL(`accounts/${route.params.accountId}/essential`)"
          :title="$t('ESSENTIAL.HEADER.BACK_TO_LIST')"
        >
          <i class="i-lucide-arrow-left size-4" />
        </router-link>
        <span class="min-w-0 text-sm font-medium truncate text-n-slate-12">
          {{ senderName(currentChat) }}
        </span>
      </div>

      <EssentialActionPanel
        v-if="currentChat.id"
        variant="compact"
        :conversation="currentChat"
        :teams="availableTeams"
      />

      <ConversationBox
        class="min-h-0 flex-1"
        :inbox-id="currentChat.inbox_id || 0"
        :is-on-expanded-layout="false"
      >
        <EssentialActionPanel
          v-if="currentChat.id"
          :conversation="currentChat"
          :teams="availableTeams"
        />
      </ConversationBox>
    </div>
  </section>
</template>
